import { useCallback } from 'react'
import client from 'api/client'
import { EventType } from 'types'

export const useOnboardingTracking = () => {
  const trackOnboardingEvent = useCallback((eventName: EventType, properties: Record<string, any> = {}) => {
    return client.trackEvent(eventName, {
      ...properties,
    })
  }, [])

  const trackGenerateContentClicked = useCallback(() => {
    return trackOnboardingEvent('Event::GenerateContentClicked', {})
  }, [trackOnboardingEvent])

  const trackWebsiteUrlSubmitted = useCallback(
    (url: string) => {
      return trackOnboardingEvent('Event::WebsiteUrlSubmitted', {
        url: url,
      })
    },
    [trackOnboardingEvent],
  )

  const trackBrandVoiceConfirmed = useCallback(() => {
    return trackOnboardingEvent('Event::BrandVoiceConfirmed', {})
  }, [trackOnboardingEvent])

  const trackBrandVoiceModalViewed = useCallback(() => {
    return trackOnboardingEvent('Event::BrandVoiceModalViewed', {})
  }, [trackOnboardingEvent])

  const trackBrandProfileEdited = useCallback(() => {
    return trackOnboardingEvent('Event::BrandProfileEdited', {})
  }, [trackOnboardingEvent])

  const trackBusinessTypeSelected = useCallback(
    (businessType: string) => {
      return trackOnboardingEvent('Event::BusinessTypeSelected', {
        business_type: businessType,
      })
    },
    [trackOnboardingEvent],
  )

  const trackMarketingBudgetSelected = useCallback(
    (marketingBudget: string) => {
      return trackOnboardingEvent('Event::MarketingBudgetSelected', {
        marketing_budget: marketingBudget,
      })
    },
    [trackOnboardingEvent],
  )

  const trackIndustryTypeSelected = useCallback(
    (industryType: string) => {
      return trackOnboardingEvent('Event::IndustryTypeSelected', {
        industry_type: industryType,
      })
    },
    [trackOnboardingEvent],
  )

  const trackAccountDetailsSubmitted = useCallback(() => {
    return trackOnboardingEvent('Event::AccountDetailsSubmitted', {})
  }, [trackOnboardingEvent])

  const trackCalendarItemsDeleted = useCallback(
    (numberOfItems: number) => {
      return trackOnboardingEvent('Event::CalendarItemsDeleted', {
        number_of_items: numberOfItems,
      })
    },
    [trackOnboardingEvent],
  )

  const trackAccountConnected = useCallback(
    (platform: string) => {
      return trackOnboardingEvent('Event::AccountConnected', {
        platform,
      })
    },
    [trackOnboardingEvent],
  )

  const trackTopicAdded = useCallback(
    (topicText: string, fieldName: string) => {
      return trackOnboardingEvent('Event::TopicAdded', {
        topic_text: topicText,
        channel: fieldName,
      })
    },
    [trackOnboardingEvent],
  )

  const trackIntegrationConnected = useCallback(
    (platform: string, success: boolean, error_type?: string) => {
      return trackOnboardingEvent('Event::OnboardingIntegrationConnected', {
        platform,
        success,
        error_type,
      })
    },
    [trackOnboardingEvent],
  )

  const trackTopicDeleted = useCallback(
    (topicText: string, fieldName: string) => {
      return trackOnboardingEvent('Event::TopicDeleted', {
        topic_text: topicText,
        channel: fieldName,
      })
    },
    [trackOnboardingEvent],
  )

  const trackAddMoreContentClicked = useCallback(
    (weekStartDate: string, weekEndDate: string) => {
      const properties = {
        week_start_date: weekStartDate,
        week_end_date: weekEndDate,
      }
      return trackOnboardingEvent('Event::AddMoreContentClicked', properties)
    },
    [trackOnboardingEvent],
  )

  const trackMarketingExperienceLevelSelected = useCallback(
    (experienceLevel: string) => {
      return trackOnboardingEvent('Event::MarketingExperienceLevelSelected', {
        marketing_experience_level: experienceLevel,
      })
    },
    [trackOnboardingEvent],
  )

  const trackEmailSubmitted = useCallback(
    (email: string) => {
      return trackOnboardingEvent('Event::EmailSubmitted', {
        email: email,
      })
    },
    [trackOnboardingEvent],
  )

  const trackOnboardingWorkspaceCreated = useCallback(() => {
    return trackOnboardingEvent('Event::OnboardingWorkspaceCreated', {})
  }, [trackOnboardingEvent])

  const trackOnboardingFlowStarted = useCallback(
    (onboardingFlow: string, flowSource: string, flowParam?: string) => {
      return trackOnboardingEvent('Event::OnboardingFlowStarted', {
        onboarding_flow: onboardingFlow,
        flow_source: flowSource,
        ...(flowParam && { flow_param: flowParam }),
      })
    },
    [trackOnboardingEvent],
  )

  const trackEditInDesignerClicked = useCallback(
    (contentType: string, entryPoint: string) => {
      return trackOnboardingEvent('Event::EditInDesignerClicked', {
        content_type: contentType,
        entry_point: entryPoint,
      })
    },
    [trackOnboardingEvent],
  )

  const trackTopicsReviewed = useCallback(() => {
    return trackOnboardingEvent('Event::TopicsReviewed', {})
  }, [trackOnboardingEvent])

  const trackEmailVerified = useCallback(() => {
    return trackOnboardingEvent('Event::EmailVerified', {})
  }, [trackOnboardingEvent])

  const trackSignupMethodSelected = useCallback(
    (signupMethod: string) => {
      return trackOnboardingEvent('Event::SignupMethodSelected', {
        signup_method: signupMethod,
      })
    },
    [trackOnboardingEvent],
  )

  const trackSupportClicked = useCallback(() => {
    return trackOnboardingEvent('Event::OnboardingSupportClicked', {})
  }, [trackOnboardingEvent])

  const trackKeywordsAdded = useCallback(
    (keywords: string[]) => {
      return trackOnboardingEvent('Event::KeywordsAdded', {
        keywords: keywords,
      })
    },
    [trackOnboardingEvent],
  )
  return {
    trackOnboardingEvent,
    trackGenerateContentClicked,
    trackWebsiteUrlSubmitted,
    trackBrandVoiceConfirmed,
    trackBrandVoiceModalViewed,
    trackBrandProfileEdited,
    trackBusinessTypeSelected,
    trackIndustryTypeSelected,
    trackMarketingBudgetSelected,
    trackAccountDetailsSubmitted,
    trackCalendarItemsDeleted,
    trackAccountConnected,
    trackTopicAdded,
    trackIntegrationConnected,
    trackTopicDeleted,
    trackAddMoreContentClicked,
    trackOnboardingWorkspaceCreated,
    trackOnboardingFlowStarted,
    trackEditInDesignerClicked,
    trackTopicsReviewed,
    trackMarketingExperienceLevelSelected,
    trackEmailSubmitted,
    trackEmailVerified,
    trackSignupMethodSelected,
    trackSupportClicked,
    trackKeywordsAdded,
  }
}
